import type { StreamDataColumn } from '@squaredup/data-streams';
import Field from 'components/forms/field/Field';
import { VisualisationOptionAction } from 'dashboard-engine/types/Visualisation';
import { OptionDropdown } from '../sharedComponents/OptionDropdown';

interface DonutColumnDropdownProps {
    label: string;
    name: string;
    help?: string;
    showNoneOption?: boolean;
    columns: StreamDataColumn[];
    /** Which option is the automatically select option, will be prefixed with 'Auto - ' */
    autoValue: string;
    builtInOptions?: { label: string; value: string }[];
    config: any;
    disabled?: boolean;
    onChange(action: VisualisationOptionAction): void;
}

const getAutoOption = ({ label, value }: { label: string; value: string }) => ({
    label: `Auto - ${label}`,
    value
});

const noneOption = {
    label: 'None',
    value: 'none'
};

/**
 * This is a slightly different version of the `ColumnDropdown` component.
 *
 * It allows additional built-in options to be added that aren't based on columns.
 *
 * @example
 * <DonutColumnDropdown
 *     label='Value'
 *     name='valueColumn'
 *     help='Select the column to use for the value of each segment'
 *     columns={valueColumns}
 *     builtInOptions={[{ label: 'Count (by Label)', value: 'countByLabel' }]}
 *     autoValue="countByLabel"
 *     config={config}
 *     onChange={onChange}
 * />
 */
export const DonutColumnDropdown: React.FC<DonutColumnDropdownProps> = ({
    label,
    name,
    help,
    showNoneOption,
    columns,
    builtInOptions = [],
    autoValue,
    config,
    disabled,
    onChange
}) => {
    const columnOptions = columns.map((c) => ({
        label: c.displayName,
        value: c.name
    }));

    /**
     * Want to make sure `Auto` option is always at top of built-in options.
     * So filter it from the existing lists and extract the auto option, prefixed with `Auto -`.
     */
    const autoOption = getAutoOption(
        [...columnOptions, ...builtInOptions].find((c) => c.value === autoValue) ?? noneOption
    );
    const otherColumns = columnOptions.filter((c) => c.value !== autoValue);
    const otherBuiltInOptions = builtInOptions.filter((o) => o.value !== autoValue);

    const optionGroups = [
        {
            label: 'Built-in',
            options: [
                ...(autoOption ? [autoOption] : []),
                ...otherBuiltInOptions,
                ...(showNoneOption ? [noneOption] : [])
            ]
        },
        {
            label: 'Columns',
            options: otherColumns
        }
    ];

    /**
     * Set default value based on the current config for that field.
     * Otherwise fallback to the auto option label.
     */
    const allOptions = [...otherColumns, ...otherBuiltInOptions, noneOption, autoOption];
    const defaultValue = allOptions.find((option) => option.value === config[name]) ?? autoOption;

    return (
        <Field label={label} htmlFor={name} help={help}>
            <OptionDropdown
                name={name}
                options={optionGroups}
                defaultValue={defaultValue}
                onChange={onChange}
                isDisabled={disabled}
            />
        </Field>
    );
};
